import { nanoid } from "nanoid";

export type SplitOrientation = "horizontal" | "vertical";

export type PaneTree =
  | { kind: "leaf"; id: string; content?: string }
  | { kind: "split"; id: string; orientation: SplitOrientation; first: PaneTree; second: PaneTree };

export const makeLeaf = (): PaneTree => ({ kind: "leaf", id: nanoid() });

export function splitLeaf(
  tree: PaneTree,
  leafId: string,
  orientation: SplitOrientation,
  newContent?: string,
): PaneTree {
  if (tree.kind === "leaf") {
    if (tree.id !== leafId) return tree;
    const second: PaneTree = { kind: "leaf", id: nanoid(), content: newContent };
    return { kind: "split", id: nanoid(), orientation, first: tree, second };
  }
  const first = splitLeaf(tree.first, leafId, orientation, newContent);
  const second = splitLeaf(tree.second, leafId, orientation, newContent);
  if (first === tree.first && second === tree.second) return tree;
  return { ...tree, first, second };
}

export function closeLeaf(tree: PaneTree, leafId: string): PaneTree | null {
  if (tree.kind === "leaf") return tree.id === leafId ? null : tree;
  const first = closeLeaf(tree.first, leafId);
  const second = closeLeaf(tree.second, leafId);
  if (first === null) return second;
  if (second === null) return first;
  if (first === tree.first && second === tree.second) return tree;
  return { ...tree, first, second };
}

export const isSingleLeaf = (tree: PaneTree): boolean => tree.kind === "leaf";

export function listLeaves(tree: PaneTree): { id: string }[] {
  if (tree.kind === "leaf") return [{ id: tree.id }];
  return [...listLeaves(tree.first), ...listLeaves(tree.second)];
}

export function findLeafByContent(tree: PaneTree, content: string): string | null {
  if (tree.kind === "leaf") return tree.content === content ? tree.id : null;
  return findLeafByContent(tree.first, content) ?? findLeafByContent(tree.second, content);
}

export function findSplitByContent(tree: PaneTree, content: string): string | null {
  if (tree.kind === "leaf") return null;
  const holdsLeaf = (node: PaneTree) => node.kind === "leaf" && node.content === content;
  if (holdsLeaf(tree.first) || holdsLeaf(tree.second)) return tree.id;
  return findSplitByContent(tree.first, content) ?? findSplitByContent(tree.second, content);
}
